import type { GlyphKey } from '../types/glyphs';

/**
 * **A CHORE'S KIND** (docs/briefs/03-chore-categories.md). Families see "category"; in code it is `kind`, because the
 * starter library's browse chips (Morning, After school, Evening, Pets) already hold the word "category" and are a
 * different thing: a template may sit under several chips, and a chore has exactly one kind.
 *
 * Kinds feed the Kredit Score's chore mix (brief 02, M) and give a Cub an icon and a spoken name per kind of chore.
 * Pure and dependency-free: the API, the mock and the app's pre-fill all call `suggestChoreKind`.
 */
export const CHORE_KINDS = ['selfCare', 'household', 'outdoor', 'petsPlants', 'helpingOthers', 'learning'] as const;
export type ChoreKind = (typeof CHORE_KINDS)[number];

/**
 * How a chore came by its kind: `suggested` by the keyword map, `unsure` when nothing matched and Household was the
 * fallback (the confirm screen asks about these first — Evan, 8 Oct), or `confirmed` by a grown-up or a club. All
 * three count the same for the score, so no child's mix drops to zero at launch.
 */
export const CHORE_KIND_STATUSES = ['suggested', 'unsure', 'confirmed'] as const;
export type ChoreKindStatus = (typeof CHORE_KIND_STATUSES)[number];

export const isChoreKind = (v: unknown): v is ChoreKind => (CHORE_KINDS as readonly unknown[]).includes(v);

/**
 * **THE ICON PER CATEGORY, so the app and the portal draw the same six** (brief 03 requirement 7; Evan, 9 Oct:
 * *"Put them in the shared icon package so both apps draw the same six."*).
 *
 * It lives in `@kk/domain` beside the kinds rather than in `@kk/ui`, for the reason the glyph vocabulary gives:
 * the API and the portal must be able to name a legal icon without depending on the mobile design system. The
 * DRAWINGS are in `@kk/ui`'s `glyphs`, and `Record<GlyphKey, Glyph>` there makes a name with no drawing a
 * compile error — so this table cannot point at nothing.
 *
 * **Four of the six are new drawings and two are not.** Household's icon is a house and Learning's is an open
 * book, and `home` and `book` already draw exactly those; this list holds one drawing per subject. The board's
 * own paths were used for all six — none of these is my drawing.
 */
export const CHORE_KIND_GLYPH = {
  selfCare: 'person',
  household: 'home',
  outdoor: 'sun',
  petsPlants: 'paw',
  helpingOthers: 'heart',
  learning: 'book',
} as const satisfies Record<ChoreKind, GlyphKey>;

/** The name a family sees, and the name a Cub hears read aloud (Evan, 8 Oct 2026). Spanish: docs/handoff/spanish/. */
export const CHORE_KIND_NAMES: Readonly<Record<ChoreKind, { name: string; spoken: string }>> = {
  selfCare: { name: 'Self-care', spoken: 'Taking care of me' },
  household: { name: 'Household', spoken: 'Around the house' },
  outdoor: { name: 'Outdoor', spoken: 'Outside' },
  petsPlants: { name: 'Pets & plants', spoken: 'Pets and plants' },
  helpingOthers: { name: 'Helping others', spoken: 'Helping someone' },
  learning: { name: 'Learning', spoken: 'Learning' },
};

/**
 * The brief's starter keyword map, with what Evan added on 9 Oct 2026: "dressed" and "reading" (other -ed and -ing
 * endings are deliberately NOT matched — "caring" would be "car"), and "help a", "help my", "help your". A keyword of
 * two words must match both, in a row.
 */
export const CHORE_KIND_KEYWORDS: Readonly<Record<ChoreKind, readonly string[]>> = {
  selfCare: ['bed', 'teeth', 'brush', 'shower', 'bath', 'dress', 'dressed', 'backpack', 'bag', 'pack', 'room'],
  household: ['dish', 'laundry', 'trash', 'garbage', 'table', 'vacuum', 'sweep', 'mop', 'kitchen', 'house', 'clean house'],
  outdoor: ['yard', 'rake', 'leaves', 'mow', 'lawn', 'snow', 'shovel', 'car'],
  petsPlants: ['dog', 'cat', 'pet', 'fish', 'litter', 'feed', 'plant', 'garden', 'water the'],
  helpingOthers: ['help', 'help a', 'help my', 'help your', 'sibling', 'brother', 'sister', 'grandma', 'grandpa', 'neighbor', 'babysit'],
  learning: ['read', 'reading', 'homework', 'study', 'practice', 'piano', 'lesson', 'spelling'],
};

/**
 * **THE SPANISH KEYWORDS** (Evan, 9 Oct 2026): families in Spanish name chores in Spanish. Matched for EVERY family
 * whatever their language, in the same pass as the English ones, with case and accents folded ("bano" is "baño").
 * US Spanish, as docs/handoff/spanish/glossary.csv has it (carro, cuarto, tender la cama, celular).
 *
 * **"tarea" alone is NOT here**: it is the app's own Spanish word for a chore, so "Tarea de la tarde" says nothing
 * about schoolwork. Homework is matched the way it is said: "hacer la tarea", "tarea de la escuela".
 * "baño" is a bath (Self-care) on its own and the bathroom (Household) in "limpiar el baño" — the longer keyword first.
 *
 * Left out on purpose (Evan, 9 Oct): "cepillar" ("cepillar al perro" is not Self-care), "ayuda" and "ayudar"
 * ("ayudar en casa" is not helping a person — the family words and "cuidar a" catch that), and "hojas" alone (it
 * also means worksheets; "recoger las hojas" is the yard).
 */
export const CHORE_KIND_KEYWORDS_ES: Readonly<Record<ChoreKind, readonly string[]>> = {
  selfCare: [
    'cama', 'tender la cama', 'dientes', 'cepillarse', 'cepillarte', 'ducha', 'ducharse', 'baño', 'bañarse',
    'bañarte', 'vestirse', 'vestirte', 'mochila', 'cuarto', 'recámara', 'habitación',
  ],
  household: [
    'plato', 'trastes', 'lavar la ropa', 'doblar la ropa', 'lavandería', 'basura', 'mesa', 'aspirar', 'aspiradora',
    'barrer', 'trapear', 'cocina', 'casa', 'limpiar la casa', 'limpiar el baño', 'limpiar baño',
  ],
  outdoor: ['patio', 'yarda', 'rastrillar', 'recoger las hojas', 'pasto', 'césped', 'cortar el pasto', 'nieve', 'palear', 'carro'],
  petsPlants: ['perro', 'gato', 'mascota', 'pez', 'peces', 'arenero', 'alimentar', 'dar de comer', 'planta', 'jardín', 'regar'],
  helpingOthers: [
    'hermano', 'hermana', 'hermanito', 'hermanita', 'abuela', 'abuelo', 'abuelita', 'abuelito',
    'vecino', 'vecina', 'cuidar a',
  ],
  learning: [
    'leer', 'lectura', 'estudiar', 'practicar', 'piano', 'lección', 'ortografía', 'hacer la tarea', 'tarea de la escuela',
    'tarea escolar',
  ],
};

/** The kind suggested when nothing matches. */
export const CHORE_KIND_FALLBACK: ChoreKind = 'household';

/** Lower case, accents folded (ñ → n, é → e), then the words: letters and digits only. */
const words = (text: string): string[] =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .match(/[a-z0-9]+/g) ?? [];

/** The same word, or its plain plural: "dishes" is "dish", "bags" is "bag" — and "garbage" is never "bag". */
const sameWord = (titleWord: string, keyword: string): boolean =>
  titleWord === keyword || titleWord === `${keyword}s` || titleWord === `${keyword}es`;

/**
 * **THE ORDER KEYWORDS ARE TRIED IN** (Evan, 9 Oct 2026): every keyword of several words before any single word, and
 * within each group the longest first; equal lengths keep the map's own order. So "Help a sibling with homework" is
 * Helping others by "help a", where "homework" alone — longer than "sibling" — would have made it Learning.
 */
const ORDERED: ReadonlyArray<{ kind: ChoreKind; words: string[]; length: number }> = [CHORE_KIND_KEYWORDS, CHORE_KIND_KEYWORDS_ES]
  .flatMap((map) =>
    CHORE_KINDS.flatMap((kind) => map[kind].map((keyword) => ({ kind, words: words(keyword), length: keyword.length }))),
  )
  .sort((a, b) => Number(b.words.length > 1) - Number(a.words.length > 1) || b.length - a.length);

/**
 * **THE SUGGESTION** (brief 03, Requirements 2): whole words of the chore's name against the keyword maps, English
 * and Spanish, case and accents folded; keywords of several words first, then single words, longest first within
 * each; a keyword never matches inside another word. No match → Household, and
 * `confident: false` so the parent is asked.
 */
export function suggestChoreKind(title: string): { kind: ChoreKind; confident: boolean } {
  const title_ = words(title);
  for (const entry of ORDERED) {
    for (let i = 0; i + entry.words.length <= title_.length; i += 1)
      if (entry.words.every((w, j) => sameWord(title_[i + j]!, w))) return { kind: entry.kind, confident: true };
  }
  return { kind: CHORE_KIND_FALLBACK, confident: false };
}

/** The status a suggestion is stored with. */
export const suggestedKindStatus = (confident: boolean): ChoreKindStatus => (confident ? 'suggested' : 'unsure');

/**
 * The kind a chore (or a club's task) is stored with: the one somebody chose, `confirmed` — or, with none chosen, the
 * suggestion from its name, `suggested`, or `unsure` when nothing matched.
 */
export function kindFor(title: string, chosen: string | null | undefined): { kind: ChoreKind; kindStatus: ChoreKindStatus } {
  if (isChoreKind(chosen)) return { kind: chosen, kindStatus: 'confirmed' };
  const s = suggestChoreKind(title);
  return { kind: s.kind, kindStatus: suggestedKindStatus(s.confident) };
}
